// tslint:disable:no-string-literal
import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { UUID } from 'angular2-uuid';
import { NavController } from 'ionic-angular';
import { isDefined } from 'ionic-angular/util/util';
import * as Q from 'q';
import { Subject } from 'rxjs/Subject';
import { inArray } from 'tinymce';
import { isArray } from 'util';
import { FileManagerService } from '../../../app/services/file-manager.service';
import { BaseObject } from '../../../core/business-objects/base-object/base-object.model';
import { entityFactory } from '../../../core/helpers/entity-factory';
import { Model } from '../../../core/util/model';
import { Control, ControlInlined } from '../../business-objects/control/control.model';
import { Field } from '../../business-objects/field/field.model';
import { Form, FormInlined } from '../../business-objects/form/form.model';
import { Group, GroupInlined } from '../../business-objects/group/group.model';
import { Job } from '../../business-objects/job/job.model';
import { Membership } from '../../business-objects/membership/membership.model';
import { CONTROL_TYPES } from '../../controls/control-types';
import { FormPage } from '../../pages/form/form';
import { FormHelper } from '../../pages/form/form-helper';
import { ValidatorsFactory } from '../../validators/validators-factory';
import { VasAppFacade } from '../vas-app-facade.service';
import { FormEvents } from './form-events';
import { FormResult } from './form-result';

export class DisplayFormArgs {

    constructor(protected form: FormInlined, protected data: BaseObject, protected formGroup: FormGroup) {

    }

    getForm(): FormInlined {
        return this.form;
    }

    getFormGroup(): FormGroup {
        return this.formGroup;
    }

    getData(): BaseObject {
        return this.data;
    }
}

@Injectable()
export class FormHelperService {

    protected ngUnsubscribe: Subject<undefined> = new Subject<undefined>();
    jobs: Array<Job>;

    formsInlined: Array<FormInlined>;
    forms: Array<Form>;
    groups: Array<Group>;
    controls: Array<Control>;
    logging: false;

    jobProperties = [
        'accessGroup',
        'account',
        'assigneeId',
        'assigneeName',
        'id',
        'jobStatus',
        'jobType',
        'version'
    ];

    jobPropertiesRequired = [
        'accessGroup',
        'account',
        'id',
        'jobStatus',
        'jobType'
    ];

    static getFormGroupErrors(formGroup: FormGroup): Array<string> {
        const errors = [];

        if (!formGroup.valid) {
            Object.keys(formGroup.controls).map(name => {
                const controlErrors = formGroup.get(name).errors;

                if (!controlErrors) return;

                Object.keys(controlErrors).forEach(errorKey => {
                    errors.push(FormHelperService.getValidatorErrorMessage(name, errorKey, controlErrors[errorKey]));
                });
            });
        }

        return errors;
    }

    static getValidatorErrorMessage(propertyLabel: string, validatorKey: string, validatorValues?: any): string {
        const config = {
            email: `"${propertyLabel}" is not a valid email address`,
            required: `this value is required`,
            invalidCreditCard: 'Invalid credit card number',
            invalidEmailAddress: `"${propertyLabel}" is not a valid email address`,
            invalidPassword: 'Invalid password. Password must be at least 6 characters long, and contain a number.',
            minlength: `Too short ${validatorValues.actualLength}/${validatorValues.requiredLength}`,
            maxlength: `Too long ${validatorValues.actualLength}/${validatorValues.requiredLength}`,
            alphabeticalCharacterRule: `At least ${validatorValues.required} alphabetical character is required`,
            digitCharacterRule: `At least ${validatorValues.required} digit is required`,
            lowercaseCharacterRule: `At least ${validatorValues.required} lower case character is required`,
            uppercaseCharacterRule: `At least ${validatorValues.required} upper case character is required`,
            specialCharacterRule: `At least ${validatorValues.required} special character is required`,
            mismatchedPasswords: 'Passwords do not match',
            normalEmailRule: 'Not a valid email address',
            cannotDetermineMime: 'Cannot determine file type',
            invalidMime: 'Invalid file type',
            editControlName: 'A control with this name used already exists'
        };

        if (!config[validatorKey]) {
            console.warn('no error message defined for %s', validatorKey, validatorValues);
        }

        return config[validatorKey];
    }

    constructor(
        protected appFacade: VasAppFacade,
        protected validatorsFactory: ValidatorsFactory,
        protected fileManagerService: FileManagerService
    ) {

        this.appFacade.objects.job.store
            .getAll()
            .takeUntil(this.ngUnsubscribe)
            .subscribe(jobs => {
                this.jobs = jobs;
            });

        this.appFacade.objects.form.store
            .getAllInlined()
            .takeUntil(this.ngUnsubscribe)
            .subscribe(list => this.formsInlined = list);

        this.appFacade.objects.form.store
            .getAll()
            .takeUntil(this.ngUnsubscribe)
            .subscribe(list => this.forms = list);

        this.appFacade.objects.group.store
            .getAll()
            .takeUntil(this.ngUnsubscribe)
            .subscribe(list => this.groups = list);

        this.appFacade.objects.control.store
            .getAll()
            .takeUntil(this.ngUnsubscribe)
            .subscribe(list => this.controls = list);
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    getUUID(): string {
        return UUID.UUID();
    }

    /**
     *
     * @param form
     * @returns {Form}
     * @todo return an FormInline
     */
    inlineForm(form: Form): FormInlined {

        // check if the form is already in lined - v hacky :(
        if (form.groups.length && isDefined((form.groups[0] as any).name)) {
            return form as any;
        }

        const formInlined = this.formsInlined.find(fe => fe.id === form.id);

        if (!formInlined) {
            throw new Error('invalid form. not found in expanded forms list');
        }

        return formInlined;
    }

    /**
     * Use this for preparing the form entryPoint for rendering.
     *
     * Parses the Form (models) and returns a FormGroup which contains the FormGroups and FormControls
     *
     * @param form: Form
     * @param data: {}
     * @returns {FormGroup}
     */
    parseFormDefinition(form: FormInlined, data: Job): Promise<FormGroup> {

        try {
            const fg = new FormBuilder().group(this._parse(form, data));

            this.addJobControls(fg, data);

            return Promise.resolve(fg);
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * This method checks the FormGroup to ensure that the FormControls that
     * must be present for every job are present.
     *
     * This means we do not need to add them as hidden controls in the
     * FormManager
     *
     * @param formGroup {FormGroup}
     * @param data {Job}
     * @protected
     */
    protected addJobControls(formGroup: FormGroup, data: Job): void {

        this.jobProperties.forEach(property => {
            if (!FormHelper.findFormControl(property, formGroup)) {
                formGroup.addControl(property, new FormControl(
                    data[property],
                    inArray(property, this.jobPropertiesRequired) ?
                        Validators.required :
                        undefined
                ));
            }
        });
    }

    _parse(form: FormInlined, data: Job): any {

        const formGroups = {};

        form.groups.forEach((group: GroupInlined): void => {
            formGroups[group.name] = this._parseGroup(group, data);
        });
        return formGroups;
    }

    _parseGroup(group: GroupInlined, data: Job): FormGroup {
        const fb = new FormBuilder();

        const fg = {};

        const name = group.name;

        if (!name) {
            throw new Error(`invalid form definition. key is missing`);
        }

        group.controls.forEach(control => {

            const defaultValue = control.defaultValue;
            let value;
            let model;

            // if the name of the control contains a dot it means it's a deep value and its value will
            // be contained within a field rather than a property of the data object.
            if (control.name.match(/\./)) {
                const parts = control.name.split('.');
                const p0 = parts[0];
                const field = data[p0]
                    .find((f: Field) => f && f.control === control.id);

                // the toggle control is a special case. We do not want to set
                // the default here, and instead allow the
                // ToggleControlComponent to handle the defaultValue itself.
                // The reason for this is that ToggleControlComponent needs to
                // differentiate between restoring the value from default, or
                // restoring from a previously saved value. If we set the
                // defaultValue here, the ToggleControlComponent cannot tell the
                // difference
                // tslint:disable-next-line:prefer-conditional-expression
                if (control.controlType.name === CONTROL_TYPES.TOGGLE) {
                    value = field ? field.value : undefined;
                } else {
                    value = field ? field.value : defaultValue;
                }

            } else {
                model = new Model(data);
                value = model.get(control.name, defaultValue);
            }

            fg[control.name] = this._parseControl(control, value);
        });

        return fb.group(fg);
    }

    _parseControl(control: ControlInlined, value: string | Array<string>): FormControl | FormArray | FormGroup {
        const fb = new FormBuilder();

        const name = control.name;

        if (!name) {
            throw new Error(`invalid form definition. property name cannot be null`);
        }

        if (control.controlType.name === CONTROL_TYPES.COMPLEX) {
            if (isArray(value)) {
                return new FormArray((value as Array<any>).map(row => {
                    const group = {};

                    Object.keys(row).forEach(key => {
                        group[key] = new FormControl(row[key]);
                    });
                    return new FormGroup(group);
                }));
                // tslint:disable-next-line:unnecessary-else
            } else {
                return fb.array([]);
            }
        } else {
            if (!isDefined(control.validators) || !control.validators.length) {
                control.validators = this.fixValidator(control);
            }
            return fb.control(value, this.parseValidators(control.validators));
        }
    }

    fixValidator(control: Control | ControlInlined): Array<string> {
        switch (control.name) {
            case 'accessGroup':
            case 'jobStatus':
                return ['required'];
            default:
                return [];
        }
    }

    parseValidators(validatorKeys: Array<string>): ValidatorFn | Array<ValidatorFn> | null {

        if (!validatorKeys) {
            return undefined;
        }

        return validatorKeys.map(v => this.validatorsFactory.getValidator(v));
    }

    async submitFormResult(result: FormResult): Promise<any> {

        if (!result) {
            return Promise.resolve();
        }

        const entityKey = result.getEntity();
        const newData = result.getData();
        const oldData = result.getOriginalData();
        const form = result.getForm();

        if (!entityKey) {
            throw Error('entityName is undefined');
        }

        if (!newData) {
            throw Error('newData is undefined');
        }

        if (!oldData) {
            throw Error('oldData is undefined');
        }

        if (!form) {
            throw Error('formDefinition is undefined');
        }

        try {
            await this.appFacade.loading.showLoading('SAVING');
            await this._submitForm(entityKey, newData, oldData as any, form);
        } finally {
            await this.appFacade.loading.hideLoading();
        }
    }

    _submitForm(
        entityKey: string,
        newData: FormGroup,
        oldData: Job,
        form: FormInlined
    ): void {

        let job: Job = new Job();

        this.jobProperties.forEach(property => {
            try {
                job[property] =
                    FormHelper.findFormControl(property, newData).value;
            } catch (e) {
                throw Error(`FormControl for ${property} not found`);
            }
        });

        form.groups.forEach((group: GroupInlined) => {
            group.controls.forEach((control: ControlInlined) => {

                // determine if we need to treat this as a field
                if (control.name.match(/fields\./)) {

                    const parts = control.name.split('.');
                    const p0 = parts[0];
                    const formControl = newData.get([group.name, control.name]);
                    let value;

                    if (typeof job[p0] === 'undefined') {
                        job[p0] = [];
                    }

                    // try to find the field for this value if it existed in the
                    // previous version of the object
                    const fieldPrevious = oldData.fields.find(
                        (f: Field) => f && f.control === control.id
                    );
                    let fieldCurrent;

                    // if we find field update it
                    if (fieldPrevious) {

                        // use the formControl's value if it changed, otherwise
                        // use the previous value from the Field
                        value = formControl && formControl.dirty ?
                            formControl.value :
                            fieldPrevious.value;

                        fieldCurrent = new Field({
                            ...fieldPrevious,
                            value,
                            control: control.id,
                            job: job.id
                        });
                    } else { // else create a new one

                        // use the formControl's value if it changed, otherwise
                        // use the Control's default value
                        value = formControl && formControl.dirty ?
                            formControl.value :
                            control.defaultValue;

                        fieldCurrent = new Field({
                            id: this.getUUID(),
                            control: control.id,
                            account: oldData.account,
                            version: 1,
                            job: job.id,
                            value
                        });
                    }

                    job[p0].push(fieldCurrent);
                } else {
                    job[control.name] = this._getFormGroupValue(newData, [group.name, control.name]);
                }
            });
        });

        if (!job) {
            throw Error('Error submitting form data');
        }

        // make sure we didn't modify the created fields
        job = new Job({
            ...job,
            created: oldData.created,
            createdBy: oldData.createdBy
        });

        try {
            this.saveObject(job);
        } catch (e) {
            console.error('Error saving %s:', entityKey, e);
        }

        this.handleFiles();

        this.submitFormMembership(job);
    }

    handleFiles(): void {
        this.fileManagerService.submit();
    }

    _getFormGroupValue(formGroup: FormGroup, args: Array<string>): any {
        const control = formGroup.get(args);
        if (!control) {
            console.warn(`control ${args.join(', ')} not found`);
            return undefined;
        }

        return control.value;
    }

    saveObject(data: Job): void {
        this.appFacade.objects.job.store.upsert(data);
    }

    removeObject(entityKey: string, data: any): void {
        const instance = entityFactory(entityKey, data);
        this.appFacade.objects.removeObject(entityKey, instance);
    }

    submitFormMembership(object: object): void {

        if (object['membership']) {
            const membership = object['membership'] as Membership;
            this.appFacade.objects.membership.store.upsert(membership);
        }
    }

    openFormPage(formEvents: FormEvents, navController: NavController): Promise<any> {
        let args: DisplayFormArgs;
        return [
            () => {
                this.logging && console.log('getDisplayFormArgs');
                return this.getDisplayFormArgs(formEvents);
            },
            _displayFormArgs => {
                args = _displayFormArgs;
                return Promise.resolve();
            },
            () => {
                this.logging && console.log('onBeforeDisplayForm');
                return formEvents.onBeforeDisplayForm(args.getFormGroup(), args.getForm());
            },
            () => {
                console.log('navController.push()', args);
                return navController.push(FormPage, {
                    formGroup: args.getFormGroup(),
                    data: args.getData(),
                    form: args.getForm(),
                    formEvents
                });
            },
            () => {
                this.logging && console.log('onAfterDisplayForm');
                return formEvents.onAfterDisplayForm(args.getFormGroup(), args.getForm());
            }
        ].reduce<any>(Q.when, Q()).catch(
            error => {
                this.appFacade.loading.hideLoading();

                if (error) {
                    this.appFacade.toast.toastDanger(error);
                }
            }
        );
    }

    protected getDisplayFormArgs(formEvents: FormEvents): Promise<DisplayFormArgs> {
        let formGroup: FormGroup;
        let data: BaseObject;
        let form: FormInlined;

        return new Promise((resolve, reject) => {
            [
                () => {
                    return formEvents.onPrepareData();
                },
                _data => {
                    data = _data;

                    if (!data) {
                        return Promise.reject(new Error('data is not an instance of BaseObject'));
                    }

                    return Promise.resolve();
                },
                () => formEvents.onPrepareForm(formEvents.getSourceEvent()),
                _form => {

                    if (!_form) {
                        return Promise.reject(undefined);
                    }

                    form = _form;
                    return Promise.resolve();
                },
                () => this.parseFormDefinition(form, data as any),
                _formGroup => {
                    formGroup = _formGroup;
                    return Promise.resolve();
                }
            ].reduce<any>(Q.when, Q()).then(
                () => resolve(new DisplayFormArgs(form, data, formGroup)),
                reject
            );
        });
    }
// tslint:disable-next-line:max-file-line-count
}



// WEBPACK FOOTER //
// ./src/vas/services/form-helper-service/form-helper.service.ts